import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    collection,
    getCountFromServer,
    getDoc,
    getDocs,
    doc,
    limit,
    orderBy,
    query,
    where,
} from 'firebase/firestore';
import {
    FaArrowRight,
    FaBell,
    FaCalendarAlt,
    FaCheckCircle,
    FaClock,
    FaCommentAlt,
    FaExclamationCircle,
    FaMobileAlt,
    FaMosque,
    FaPlus,
    FaSyncAlt,
} from 'react-icons/fa';

import { db } from '../utils/FirebaseConfig';
import { fetchPublishedSystemUpdates } from '../utils/systemUpdates';
import '../styles/Dashboard.css';

const DASHBOARD_CACHE_MS = 5 * 60 * 1000;

const toDate = (value) => {
    if (!value) return null;
    if (typeof value?.toDate === 'function') return value.toDate();
    if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const startOfDay = (date = new Date()) => {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
};

const endOfDay = (date = new Date()) => {
    const next = new Date(date);
    next.setHours(23, 59, 59, 999);
    return next;
};

const formatDate = (date) => new Intl.DateTimeFormat('ms-MY', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Asia/Kuala_Lumpur',
}).format(date);

const formatShortDate = (date) => new Intl.DateTimeFormat('ms-MY', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'Asia/Kuala_Lumpur',
}).format(date);

const formatTime = (date) => new Intl.DateTimeFormat('ms-MY', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Kuala_Lumpur',
}).format(date);

const ymd = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const parseYmd = (value) => {
    const [year, month, day] = String(value || '').split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
};

const getNthWeekday = (year, month, weekday, nth) => {
    if (nth === -1) {
        const last = new Date(year, month + 1, 0);
        last.setDate(last.getDate() - ((last.getDay() - weekday + 7) % 7));
        return last;
    }

    const first = new Date(year, month, 1);
    const day = 1 + ((weekday - first.getDay() + 7) % 7) + ((nth - 1) * 7);
    const result = new Date(year, month, day);
    return result.getMonth() === month ? result : null;
};

const expandRecurringRule = (rule, rangeStart, rangeEnd) => {
    const repeatStart = parseYmd(rule.repeatStart);
    const repeatEnd = parseYmd(rule.repeatEnd);
    if (!repeatStart || !repeatEnd) return [];

    const start = repeatStart > rangeStart ? repeatStart : rangeStart;
    const end = repeatEnd < rangeEnd ? repeatEnd : rangeEnd;
    if (start > end) return [];

    const exceptions = new Set([
        ...(Array.isArray(rule.exDates) ? rule.exDates : []),
        ...(Array.isArray(rule.exceptions) ? rule.exceptions : []),
    ]);
    const results = [];
    const pushOccurrence = (date) => {
        if (!date || date < start || date > end || exceptions.has(ymd(date))) return;
        const [hour, minute] = String(rule.time || '00:00').split(':').map(Number);
        const occurrence = new Date(date);
        occurrence.setHours(hour || 0, minute || 0, 0, 0);
        results.push({
            id: `${rule.seriesId || rule.id || rule.title}-${ymd(date)}`,
            title: rule.title || (rule.isKuliah ? 'Kuliah' : 'Program'),
            start: occurrence,
            type: rule.isKuliah ? 'kuliah' : 'program',
            speaker: rule.speaker || '',
            recurring: true,
        });
    };

    if (rule.repeat === 'weekly') {
        const weekday = Number(rule.repeatDow ?? repeatStart.getDay());
        const cursor = new Date(start);
        cursor.setDate(cursor.getDate() + ((weekday - cursor.getDay() + 7) % 7));
        while (cursor <= end) {
            pushOccurrence(new Date(cursor));
            cursor.setDate(cursor.getDate() + 7);
        }
    } else if (rule.repeat === 'monthly') {
        const weekday = Number(rule.repeatDow ?? repeatStart.getDay());
        const nth = Number(rule.repeatNth ?? Math.ceil(repeatStart.getDate() / 7));
        const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
        while (cursor <= end) {
            pushOccurrence(getNthWeekday(cursor.getFullYear(), cursor.getMonth(), weekday, nth));
            cursor.setMonth(cursor.getMonth() + 1);
        }
    }

    return results;
};

const uniqueEvents = (events) => {
    const seen = new Set();
    return events
        .filter((event) => event.start instanceof Date && !Number.isNaN(event.start.getTime()))
        .sort((a, b) => a.start - b.start)
        .filter((event) => {
            const key = `${event.id || ''}|${event.title}|${event.start.getTime()}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
};

const serializeDashboard = (data) => ({
    ...data,
    upcoming: data.upcoming.map((item) => ({ ...item, start: item.start.toISOString() })),
    updates: data.updates.map((item) => ({
        ...item,
        publishedAt: item.publishedAt instanceof Date ? item.publishedAt.toISOString() : item.publishedAt,
    })),
});

const restoreDashboard = (data) => ({
    ...data,
    upcoming: (data.upcoming || []).map((item) => ({ ...item, start: toDate(item.start) })),
    updates: (data.updates || []).map((item) => ({ ...item, publishedAt: toDate(item.publishedAt) })),
});

function Dashboard({ mosqueId, subscriptionEnd }) {
    const userProfile = useMemo(
        () => JSON.parse(localStorage.getItem('userProfile') || '{}'),
        []
    );
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [data, setData] = useState({
        mosqueName: userProfile.namaMasjid || 'Masjid / Surau',
        todayCount: 0,
        weekCount: 0,
        activeAnnouncements: 0,
        deviceCount: null,
        upcoming: [],
        attention: [],
        updates: [],
    });

    const loadDashboard = useCallback(async ({ force = false } = {}) => {
        if (!mosqueId) return;
        const cacheKey = `dashboard:v2:${mosqueId}`;

        if (!force) {
            try {
                const cached = JSON.parse(sessionStorage.getItem(cacheKey) || 'null');
                if (cached?.savedAt && Date.now() - cached.savedAt < DASHBOARD_CACHE_MS) {
                    const restored = restoreDashboard(cached.data);
                    setData(restored);
                    setLoading(false);

                    // Statistik masjid boleh menggunakan cache untuk menjimatkan
                    // bacaan, tetapi makluman Etikaf mesti sentiasa disemak semula.
                    fetchPublishedSystemUpdates()
                        .then((items) => items.map((update) => ({
                            ...update,
                            publishedAt: toDate(update.publishedAt),
                        })))
                        .then((updates) => {
                            setData((current) => ({ ...current, updates }));
                            sessionStorage.setItem(cacheKey, JSON.stringify({
                                savedAt: cached.savedAt,
                                data: serializeDashboard({ ...restored, updates }),
                            }));
                        })
                        .catch((error) => {
                            console.warn('[Dashboard] Feed kemas kini belum tersedia:', error?.code || error);
                        });
                    return;
                }
            } catch (_) {
                sessionStorage.removeItem(cacheKey);
            }
        }

        force ? setRefreshing(true) : setLoading(true);

        const todayStart = startOfDay();
        const todayEnd = endOfDay();
        const weekEnd = endOfDay(new Date(todayStart.getTime() + (7 * 24 * 60 * 60 * 1000)));

        try {
            const mosqueRef = doc(db, 'masajid', mosqueId);
            const mosquePromise = getDoc(mosqueRef);

            const eventQuery = (collectionName) => query(
                collection(db, 'masajid', mosqueId, collectionName),
                where('start', '>=', todayStart),
                where('start', '<=', weekEnd),
                orderBy('start', 'asc'),
                limit(20)
            );

            const recurringQuery = (collectionName) => query(
                collection(db, 'masajid', mosqueId, collectionName),
                where('repeatEnd', '>=', ymd(todayStart)),
                orderBy('repeatEnd', 'asc'),
                limit(20)
            );

            const announcementsQuery = query(
                collection(db, 'masajid', mosqueId, 'announcements'),
                orderBy('createdAt', 'desc'),
                limit(20)
            );

            const safeDocs = async (promise) => {
                try {
                    return await promise;
                } catch (error) {
                    console.warn('[Dashboard] Bacaan pilihan tidak tersedia:', error?.code || error);
                    return null;
                }
            };

            const [
                mosqueSnap,
                kuliahSnap,
                programSnap,
                kuliahRecurringSnap,
                programRecurringSnap,
                announcementSnap,
            ] = await Promise.all([
                mosquePromise,
                safeDocs(getDocs(eventQuery('kuliah'))),
                safeDocs(getDocs(eventQuery('program'))),
                safeDocs(getDocs(recurringQuery('kuliahRecurring'))),
                safeDocs(getDocs(recurringQuery('programRecurring'))),
                safeDocs(getDocs(announcementsQuery)),
            ]);

            const mosqueData = mosqueSnap.exists() ? mosqueSnap.data() : {};
            const profile = mosqueData.profail || {};

            const mapSingleEvents = (snapshot, type) => snapshot?.docs.map((eventDoc) => {
                const event = eventDoc.data() || {};
                return {
                    id: eventDoc.id,
                    title: event.title || (type === 'kuliah' ? 'Kuliah' : 'Program'),
                    start: toDate(event.start),
                    type,
                    speaker: event.speaker || '',
                };
            }) || [];

            const recurringEvents = [
                ...(kuliahRecurringSnap?.docs || []).flatMap((ruleDoc) =>
                    expandRecurringRule({ id: ruleDoc.id, ...ruleDoc.data(), isKuliah: true }, todayStart, weekEnd)
                ),
                ...(programRecurringSnap?.docs || []).flatMap((ruleDoc) =>
                    expandRecurringRule({ id: ruleDoc.id, ...ruleDoc.data(), isKuliah: false }, todayStart, weekEnd)
                ),
            ];

            const legacyEvents = [
                ...(mosqueData.aktiviti?.kuliah || []).map((event) => ({
                    id: event.eventId,
                    title: event.title || 'Kuliah',
                    start: toDate(event.start),
                    type: 'kuliah',
                    speaker: event.speaker || '',
                })),
                ...(mosqueData.aktiviti?.program || []).map((event) => ({
                    id: event.eventId,
                    title: event.title || 'Program',
                    start: toDate(event.start),
                    type: 'program',
                    speaker: '',
                })),
            ].filter((event) => event.start >= todayStart && event.start <= weekEnd);

            const allEvents = uniqueEvents([
                ...mapSingleEvents(kuliahSnap, 'kuliah'),
                ...mapSingleEvents(programSnap, 'program'),
                ...recurringEvents,
                ...legacyEvents,
            ]);

            const announcements = (announcementSnap?.docs || []).map((announcementDoc) => ({
                id: announcementDoc.id,
                ...announcementDoc.data(),
            }));
            const isActiveAnnouncement = (announcement) => {
                if ((announcement.status || 'published') !== 'published') return false;
                const expiresAt = toDate(announcement.expiresAt);
                return !expiresAt || expiresAt >= new Date();
            };
            const activeAnnouncements = announcements.filter(isActiveAnnouncement);

            let deviceCount = null;
            try {
                const deviceQuery = query(
                    collection(db, 'devices'),
                    where('selectedMasjidId', '==', mosqueId)
                );
                const deviceAggregate = await getCountFromServer(deviceQuery);
                deviceCount = deviceAggregate.data().count;
            } catch (error) {
                console.warn('[Dashboard] Kiraan peranti belum dibenarkan:', error?.code || error);
            }

            let updates = [];
            try {
                updates = (await fetchPublishedSystemUpdates())
                    .map((update) => ({
                        ...update,
                        publishedAt: toDate(update.publishedAt),
                    }));
            } catch (error) {
                console.warn('[Dashboard] Feed kemas kini belum tersedia:', error?.code || error);
            }
            const requiredProfile = [profile.nama, profile.alamat, profile.poskod, profile.negeri, profile.no_tel, profile.logo];
            const profilePercent = Math.round(
                (requiredProfile.filter((value) => String(value || '').trim()).length / requiredProfile.length) * 100
            );
            const attention = [];

            if (profilePercent < 100) {
                attention.push({
                    id: 'profile',
                    title: `Profil masjid ${profilePercent}% lengkap`,
                    detail: 'Lengkapkan alamat, telefon dan gambar supaya maklumat jemaah sentiasa tepat.',
                    actionLabel: 'Lengkapkan profil',
                    actionUrl: '/profail',
                });
            }
            if (allEvents.length === 0) {
                attention.push({
                    id: 'schedule',
                    title: 'Tiada aktiviti untuk 7 hari akan datang',
                    detail: 'Tambah kuliah atau program supaya jemaah dapat melihat jadual terkini.',
                    actionLabel: 'Tambah aktiviti',
                    actionUrl: '/utama?new=1',
                });
            }

            const expiry = toDate(subscriptionEnd);
            if (expiry) {
                const daysLeft = Math.ceil((expiry.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
                if (daysLeft <= 30) {
                    attention.push({
                        id: 'subscription',
                        title: daysLeft > 0 ? `Langganan tamat dalam ${daysLeft} hari` : 'Langganan telah tamat',
                        detail: 'Admin dan ETV menggunakan tempoh langganan yang sama. Perbaharui lebih awal supaya operasi tidak terhenti.',
                        actionLabel: 'Perbaharui sekarang',
                        actionUrl: '/subscription-expired',
                    });
                }
            }

            const nextData = {
                mosqueName: profile.nama || userProfile.namaMasjid || 'Masjid / Surau',
                todayCount: allEvents.filter((event) => event.start >= todayStart && event.start <= todayEnd).length,
                weekCount: allEvents.length,
                activeAnnouncements: activeAnnouncements.length,
                funeralToday: activeAnnouncements.filter((announcement) =>
                    announcement.type === 'funeral_prayer' && announcement.dateYMD === ymd(todayStart)
                ).length,
                deviceCount,
                upcoming: allEvents.slice(0, 5),
                attention: attention.slice(0, 3),
                updates,
            };

            setData(nextData);
            sessionStorage.setItem(cacheKey, JSON.stringify({
                savedAt: Date.now(),
                data: serializeDashboard(nextData),
            }));
        } catch (error) {
            console.error('[Dashboard] Gagal memuatkan dashboard:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [mosqueId, subscriptionEnd, userProfile.namaMasjid]);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const expiryDate = toDate(subscriptionEnd);
    const subscriptionDaysLeft = expiryDate
        ? Math.ceil((expiryDate.getTime() - Date.now()) / (24 * 60 * 60 * 1000))
        : null;
    const showSubscriptionWarning = subscriptionDaysLeft !== null && subscriptionDaysLeft <= 30;
    const subscriptionWarningTone = subscriptionDaysLeft <= 7
        ? 'urgent'
        : subscriptionDaysLeft <= 14
            ? 'attention'
            : 'notice';
    const statCards = [
        { label: 'Aktiviti hari ini', value: data.todayCount, icon: <FaCalendarAlt />, tone: 'purple' },
        { label: '7 hari akan datang', value: data.weekCount, icon: <FaClock />, tone: 'blue' },
        { label: 'Pengumuman aktif', value: data.activeAnnouncements, icon: <FaCommentAlt />, tone: 'rose' },
        {
            label: 'Peranti mengikuti',
            value: data.deviceCount === null ? '—' : data.deviceCount,
            icon: <FaMobileAlt />,
            tone: 'green',
            hint: data.deviceCount === null ? 'Kiraan akan muncul selepas akses data peranti disambungkan.' : null,
        },
    ];

    return (
        <div className="admin-dashboard">
            <header className="dashboard-hero">
                <div>
                    <span className="dashboard-eyebrow">DASHBOARD MASJID</span>
                    <h1>Assalamualaikum, {data.mosqueName}</h1>
                    <p>{formatDate(new Date())}</p>
                </div>
                <div className="dashboard-hero-actions">
                    {expiryDate && (
                        <div className="subscription-chip">
                            <span>Langganan aktif sehingga</span>
                            <strong>{new Intl.DateTimeFormat('ms-MY', {
                                day: 'numeric', month: 'short', year: 'numeric'
                            }).format(expiryDate)}</strong>
                        </div>
                    )}
                    <button
                        type="button"
                        className="dashboard-refresh"
                        onClick={() => loadDashboard({ force: true })}
                        disabled={refreshing}
                        aria-label="Muat semula dashboard"
                    >
                        <FaSyncAlt className={refreshing ? 'is-spinning' : ''} />
                    </button>
                </div>
            </header>

            {showSubscriptionWarning && (
                <section className={`subscription-warning ${subscriptionWarningTone}`}>
                    <FaExclamationCircle />
                    <div>
                        <strong>
                            {subscriptionDaysLeft > 0
                                ? `Langganan Etikaf tamat dalam ${subscriptionDaysLeft} hari`
                                : 'Langganan Etikaf telah tamat'}
                        </strong>
                        <p>
                            Langganan Etikaf Admin dan ETV berkongsi tarikh tamat yang sama. Setiap bayaran RM600
                            menyambung 12 bulan daripada tarikh tamat semasa, bukan tarikh bayaran, kerana ETV terus
                            beroperasi sepanjang tempoh langganan. Selepas tamat, ETV diberi tempoh ihsan 14 hari
                            tanpa memaparkan notis bayaran kepada orang awam.
                        </p>
                    </div>
                    <Link to="/subscription-expired">Perbaharui sekarang <FaArrowRight /></Link>
                </section>
            )}

            <section className="dashboard-stats" aria-label="Ringkasan masjid">
                {statCards.map((card) => (
                    <article className="dashboard-stat" key={card.label} title={card.hint || undefined}>
                        <div className={`dashboard-stat-icon ${card.tone}`}>{card.icon}</div>
                        <div>
                            <strong>{loading ? '…' : card.value}</strong>
                            <span>{card.label}</span>
                        </div>
                    </article>
                ))}
            </section>

            {data.funeralToday > 0 && (
                <div className="funeral-dashboard-notice">
                    <FaMosque />
                    <div>
                        <strong>{data.funeralToday} makluman solat jenazah hari ini</strong>
                        <span>Semak masa dan lokasi sebelum jemaah tiba.</span>
                    </div>
                    <Link to="/mesej">Semak</Link>
                </div>
            )}

            <section className="dashboard-quick-actions" aria-label="Tindakan pantas">
                <Link to="/utama?new=1"><FaPlus /> Tambah aktiviti</Link>
                <Link to="/mesej?type=announcement&new=1"><FaBell /> Hantar pengumuman</Link>
                <Link to="/mesej?type=funeral_prayer&new=1"><FaMosque /> Solat jenazah</Link>
                <Link to="/utama"><FaCalendarAlt /> Buka kalendar</Link>
            </section>

            <div className="dashboard-content-grid">
                <section className="dashboard-panel dashboard-upcoming">
                    <div className="dashboard-panel-heading">
                        <div>
                            <span className="dashboard-section-label">JADUAL</span>
                            <h2>Hari ini dan akan datang</h2>
                        </div>
                        <Link to="/utama">Lihat kalendar <FaArrowRight /></Link>
                    </div>

                    {loading ? (
                        <div className="dashboard-skeleton-list"><span /><span /><span /></div>
                    ) : data.upcoming.length > 0 ? (
                        <div className="upcoming-list">
                            {data.upcoming.map((event) => (
                                <article className="upcoming-item" key={`${event.id}-${event.start.getTime()}`}>
                                    <div className="upcoming-date">
                                        <strong>{formatShortDate(event.start)}</strong>
                                        <span>{formatTime(event.start)}</span>
                                    </div>
                                    <div className="upcoming-copy">
                                        <span className={`event-type ${event.type}`}>{event.type === 'kuliah' ? 'Kuliah' : 'Program'}</span>
                                        <h3>{event.title}</h3>
                                        {event.speaker && <p>{event.speaker}</p>}
                                    </div>
                                </article>
                            ))}
                        </div>
                    ) : (
                        <div className="dashboard-empty">
                            <FaCalendarAlt />
                            <strong>Tiada aktiviti dijadualkan</strong>
                            <span>Jadual tujuh hari akan datang masih kosong.</span>
                            <Link to="/utama?new=1">Tambah aktiviti</Link>
                        </div>
                    )}
                </section>

                <div className="dashboard-side-column">
                    <section className="dashboard-panel dashboard-attention">
                        <div className="dashboard-panel-heading compact">
                            <div>
                                <span className="dashboard-section-label">SEMAKAN</span>
                                <h2>Perlu perhatian</h2>
                                <p className="dashboard-heading-note">Dijana daripada maklumat dan jadual masjid.</p>
                            </div>
                        </div>

                        {data.attention.length > 0 ? data.attention.map((item) => (
                            <article className="attention-item" key={item.id}>
                                <FaExclamationCircle />
                                <div>
                                    <strong>{item.title}</strong>
                                    <p>{item.detail}</p>
                                    {item.actionUrl && <Link to={item.actionUrl}>{item.actionLabel} <FaArrowRight /></Link>}
                                </div>
                            </article>
                        )) : (
                            <div className="attention-clear">
                                <FaCheckCircle />
                                <div>
                                    <strong>Semua maklumat utama teratur</strong>
                                    <span>Tiada perkara mendesak untuk tindakan sekarang.</span>
                                </div>
                            </div>
                        )}
                    </section>

                    <section className="dashboard-panel dashboard-updates">
                        <div className="dashboard-panel-heading compact">
                            <div>
                                <span className="dashboard-section-label">DARIPADA ETIKAF</span>
                                <h2>Apa yang baharu</h2>
                            </div>
                            {data.updates.length > 0 && <Link to="/kemas-kini">Lihat semua <FaArrowRight /></Link>}
                        </div>

                        {data.updates.length > 0 ? data.updates.slice(0, 2).map((update, index) => (
                            <article className="update-item" key={update.id}>
                                <div className="update-meta">
                                    {index === 0 && <span className="new-update-badge">Baharu</span>}
                                    {update.publishedAt && <span>{formatShortDate(update.publishedAt)}</span>}
                                </div>
                                <h3>{update.title}</h3>
                                <p>{update.summary || update.description || ''}</p>
                                {update.actionUrl && (
                                    <Link to={update.actionUrl}>{update.actionLabel || 'Ketahui lebih lanjut'} <FaArrowRight /></Link>
                                )}
                            </article>
                        )) : (
                            <div className="updates-empty">
                                <FaBell />
                                <span>Belum ada makluman daripada Etikaf.</span>
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
}

export default Dashboard;
